package com.karyadina.ui.screens

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons

import androidx.compose.material.icons.automirrored.outlined.MenuBook
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.karyadina.ui.components.StudyExtendedFloatingActionButton
import com.karyadina.model.*
import com.karyadina.model.Unit as StudyUnit
import kotlinx.datetime.*
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

import org.jetbrains.compose.resources.stringResource


import com.karyadina.ui.theme.StudyIcons
import com.karyadina.ui.theme.StudyStrings
import com.karyadina.util.isSystemIn24HourFormat

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TaskTrackerScreen(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val viewModel = koinViewModel<TaskTrackerViewModel>()
    val tasks by viewModel.filteredTasks.collectAsState()
    val stats by viewModel.stats.collectAsState()
    val selectedMeasure by viewModel.filter.collectAsState() // Renamed to avoid confusion if needed, or keep same
    val selectedFilter by viewModel.filter.collectAsState()
    val searchQuery by viewModel.searchQuery.collectAsState()
    val selectedTask by viewModel.selectedTask.collectAsState()
    
    var showTaskEditor by remember { mutableStateOf(false) }
    var editingTask by remember { mutableStateOf<Task?>(null) }
    var isFabVisible by remember { mutableStateOf(true) }
    val listState = androidx.compose.foundation.lazy.rememberLazyListState()
    
    // Detect scroll direction to hide/show FAB
    LaunchedEffect(Unit) {
        var previousIndex = 0
        var previousScrollOffset = 0
        
        androidx.compose.runtime.snapshotFlow { 
            listState.firstVisibleItemIndex to listState.firstVisibleItemScrollOffset
        }.collect { (currentIndex, currentOffset) ->
            if (listState.isScrollInProgress) {
                val isScrollingDown = currentIndex > previousIndex || 
                    (currentIndex == previousIndex && currentOffset > previousScrollOffset)
                isFabVisible = !isScrollingDown
                
                previousIndex = currentIndex
                previousScrollOffset = currentOffset
            }
        }
    }
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(StudyStrings.TaskTrackerTitle)) },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(StudyIcons.ArrowBack, stringResource(StudyStrings.Back))
                    }
                }
            )
        },
        floatingActionButton = {
            AnimatedVisibility(
                visible = isFabVisible,
                enter = slideInVertically(initialOffsetY = { it }) + fadeIn(),
                exit = slideOutVertically(targetOffsetY = { it }) + fadeOut()
            ) {
                StudyExtendedFloatingActionButton(
                    onClick = { 
                        editingTask = null
                        showTaskEditor = true 
                    },
                    icon = { Icon(StudyIcons.Add, null) },
                    text = { Text(stringResource(StudyStrings.TaskCreateTitle)) }
                )
            }
        }
    ) { padding ->
        Box(
            modifier = modifier
                .fillMaxSize()
                .padding(padding)
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            MaterialTheme.colorScheme.surface,
                            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
                        )
                    )
                )
        ) {
            com.karyadina.ui.components.ResponsiveContent {
                Column(
                    modifier = Modifier.fillMaxSize()
                ) {
                // Statistics Cards
                TaskStatsRow(stats, modifier = Modifier.padding(16.dp))
                
                // Search Bar
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = { viewModel.setSearchQuery(it) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp),
                    placeholder = { Text(stringResource(StudyStrings.TaskTitlePlaceholder)) }, // using placeholder title as generic search placeholder isn't defined yet, adapting for check
                    leadingIcon = { Icon(StudyIcons.Search, null) },
                    trailingIcon = if (searchQuery.isNotEmpty()) {
                        { 
                            IconButton(onClick = { viewModel.setSearchQuery("") }) {
                                Icon(StudyIcons.Clear, null)
                            }
                        }
                    } else null,
                    singleLine = true,
                    shape = CircleShape
                )
                
                Spacer(modifier = Modifier.height(12.dp))
                
                // Filter Chips
                FilterChips(
                    selectedFilter = selectedFilter,
                    onFilterChange = { 
                        viewModel.setFilter(it)
                    },
                    modifier = Modifier.fillMaxWidth() // Don't restrict horizontal padding here for lazy row
                )
                
                Spacer(modifier = Modifier.height(8.dp))
                
                // Task List
                if (tasks.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Icon(
                                StudyIcons.EmptyTasks,
                                contentDescription = null,
                                modifier = Modifier.size(64.dp),
                                tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.5f)
                            )
                            Text(
                                stringResource(StudyStrings.TaskEmptyTitle),
                                style = MaterialTheme.typography.titleMedium,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                            )
                            Text(
                                stringResource(StudyStrings.TaskEmptyDesc),
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.4f)
                            )
                        }
                    }
                } else {
                    LazyColumn(
                        state = listState,
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        items(tasks, key = { it.id }) { task ->
                            TaskCard(
                                task = task,
                                onClick = { viewModel.selectTask(task) },
                                onToggleComplete = { viewModel.toggleTaskCompletion(task) },
                                onDelete = { viewModel.deleteTask(task.id) }
                            )
                        }
                    }
                }
            }
            
            // Task Editor Dialog
            if (showTaskEditor) {
                TaskEditorDialog(
                    task = editingTask,
                    onDismiss = { showTaskEditor = false },
                    onSave = { task ->
                        if (task.id.isBlank()) {
                            viewModel.addTask(task)
                        } else {
                            viewModel.updateTask(task)
                        }
                        showTaskEditor = false
                        editingTask = null
                    }
                )
            }

            // Task Detail Dialog
            if (selectedTask != null) {
                TaskDetailDialog(
                    task = selectedTask!!,
                    onDismiss = { viewModel.selectTask(null) },
                    onToggleComplete = { viewModel.toggleTaskCompletion(selectedTask!!) },
                    onDelete = { 
                        viewModel.deleteTask(selectedTask!!.id)
                    },
                    onEdit = {
                        editingTask = selectedTask
                        showTaskEditor = true
                        viewModel.selectTask(null)
                    }
                )
            }
            }
        }
    }
}

@Composable
fun TaskStatsRow(stats: TaskStats, modifier: Modifier = Modifier) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        TaskStatCard(
            title = stringResource(StudyStrings.TaskStatsTotal),
            value = "${stats.totalTasks}",
            icon = StudyIcons.StatsTotal,
            modifier = Modifier.weight(1f)
        )
        TaskStatCard(
            title = stringResource(StudyStrings.TaskStatsDone),
            value = "${stats.completedTasks}",
            icon = StudyIcons.StatsDone,
            modifier = Modifier.weight(1f)
        )
        TaskStatCard(
            title = stringResource(StudyStrings.TaskStatsOverdue),
            value = "${stats.overdueTasks}",
            icon = StudyIcons.StatsOverdue,
            modifier = Modifier.weight(1f),
            color = if (stats.overdueTasks > 0) MaterialTheme.colorScheme.error else null
        )
    }
}

@Composable
fun TaskStatCard(
    title: String,
    value: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    modifier: Modifier = Modifier,
    color: Color? = null
) {
    ElevatedCard(modifier = modifier) {
        Column(
            modifier = Modifier.padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Icon(
                icon,
                contentDescription = null,
                modifier = Modifier.size(20.dp),
                tint = color ?: MaterialTheme.colorScheme.primary
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                value,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = color ?: MaterialTheme.colorScheme.onSurface
            )
            Text(
                title,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
            )
        }
    }
}

@Composable
fun FilterChips(
    selectedFilter: String,
    onFilterChange: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    // Map filter keys (for logic) to display strings (for UI)
    val filterDisplayMap = mapOf(
         "All" to stringResource(StudyStrings.TaskFilterAll),
         "Active" to stringResource(StudyStrings.TaskFilterActive),
         "Today" to stringResource(StudyStrings.TaskFilterToday),
         "Overdue" to stringResource(StudyStrings.TaskFilterOverdue),
         "Completed" to stringResource(StudyStrings.TaskFilterCompleted)
    )
    
    val filters = listOf("All", "Active", "Today", "Overdue", "Completed")

    LazyRow(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        contentPadding = PaddingValues(horizontal = 16.dp)
    ) {
        items(filters) { filterKey ->
            FilterChip(
                selected = selectedFilter == filterKey,
                onClick = { onFilterChange(filterKey) },
                label = { Text(filterDisplayMap[filterKey] ?: filterKey) },
                leadingIcon = if (selectedFilter == filterKey) {
                    { Icon(StudyIcons.Completed, null, modifier = Modifier.size(16.dp)) }
                } else null
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class)
@Composable
fun TaskCard(
    task: Task,
    onClick: () -> Unit,
    onToggleComplete: () -> Unit,
    onDelete: () -> Unit
) {
    val isCompleted = task.status == TaskStatus.COMPLETED.name
    val priorityColor = when (task.priority) {
        TaskPriority.URGENT.name -> MaterialTheme.colorScheme.error
        TaskPriority.HIGH.name -> MaterialTheme.colorScheme.tertiary
        TaskPriority.MEDIUM.name -> MaterialTheme.colorScheme.primary
        else -> MaterialTheme.colorScheme.surfaceVariant
    }
    
    val currentMillis = Clock.System.now().toEpochMilliseconds()
    val isOverdue = task.deadline != null && 
                    task.deadline!! < currentMillis &&
                    !isCompleted
    
    ElevatedCard(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier.fillMaxWidth()
        ) {
            // Priority bar
            Box(
                modifier = Modifier
                    .width(4.dp)
                    .fillMaxHeight()
                    .background(priorityColor)
            )
            
            Row(
                modifier = Modifier
                    .weight(1f)
                    .padding(12.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Checkbox
                Checkbox(
                    checked = isCompleted,
                    onCheckedChange = { onToggleComplete() }
                )
                
                // Task content
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    Text(
                        task.title,
                        style = MaterialTheme.typography.bodyLarge,
                        fontWeight = FontWeight.Medium,
                        textDecoration = if (isCompleted) TextDecoration.LineThrough else null,
                        color = if (isCompleted) 
                            MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f)
                        else MaterialTheme.colorScheme.onSurface
                    )
                    
                    if (task.description.isNotBlank()) {
                        Text(
                            task.description,
                            style = MaterialTheme.typography.bodySmall,
                            maxLines = 2,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                        )
                    }
                    
                    // Tags row
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Category
                        Surface(
                            shape = MaterialTheme.shapes.extraSmall,
                            color = MaterialTheme.colorScheme.secondaryContainer
                        ) {
                            Text(
                                stringResource(getCategoryLabel(task.category)),
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSecondaryContainer
                            )
                        }
                        
                        // Deadline
                        if (task.deadline != null) {
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(4.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    if (isOverdue) StudyIcons.Error else StudyIcons.Time,
                                    contentDescription = null,
                                    modifier = Modifier.size(14.dp),
                                    tint = if (isOverdue) 
                                        MaterialTheme.colorScheme.error
                                    else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                )
                                Text(
                                    formatDeadline(task.deadline!!),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = if (isOverdue) 
                                        MaterialTheme.colorScheme.error
                                    else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                )
                            }
                        }

                        // Recurrence
                        if (task.recurrence != "NONE") {
                            Icon(
                                StudyIcons.Recurrence,
                                contentDescription = stringResource(StudyStrings.TaskRecurrenceLabel), // Just using generic 'recurring' description
                                modifier = Modifier.size(14.dp),
                                tint = MaterialTheme.colorScheme.primary
                            )
                        }
                        
                        // Subject
                        if (task.subject != null) {
                            Surface(
                                shape = CircleShape,
                                color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f)
                            ) {
                                Text(
                                    task.subject!!,
                                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                                    maxLines = 1,
                                    overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis
                                )
                            }
                        }
                    }
                }
                
                // Delete button
                IconButton(
                    onClick = onDelete,
                    modifier = Modifier.size(36.dp)
                ) {
                    Icon(
                        StudyIcons.Delete,
                        contentDescription = stringResource(StudyStrings.ActionDelete), 
                        modifier = Modifier.size(20.dp),
                        tint = MaterialTheme.colorScheme.error
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class)
@Composable
fun TaskDetailDialog(
    task: Task,
    onDismiss: () -> Unit,
    onToggleComplete: () -> Unit,
    onDelete: () -> Unit,
    onEdit: () -> Unit
) {
    Dialog(onDismissRequest = onDismiss) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight(0.8f),
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.surface,
            tonalElevation = 6.dp
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(24.dp)
            ) {
                // Actions Header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    IconButton(onClick = onDismiss) {
                        Icon(StudyIcons.Close, stringResource(StudyStrings.ActionClose)) 
                    }
                    Row {
                        IconButton(onClick = {
                            onDelete()
                            onDismiss()
                        }) {
                            Icon(StudyIcons.Delete, stringResource(StudyStrings.ActionDelete), tint = MaterialTheme.colorScheme.error)
                        }
                        IconButton(onClick = onToggleComplete) {
                            if (task.status == TaskStatus.COMPLETED.name) {
                                Icon(StudyIcons.CheckBoxChecked, stringResource(StudyStrings.ActionMarkIncomplete), tint = MaterialTheme.colorScheme.primary)
                            } else {
                                Icon(StudyIcons.CheckBoxUnchecked, stringResource(StudyStrings.ActionMarkComplete))
                            }
                        }
                        IconButton(onClick = onEdit) {
                            Icon(StudyIcons.Edit, stringResource(StudyStrings.ActionEdit), tint = MaterialTheme.colorScheme.primary)
                        }
                    }
                }
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // Title
                Text(
                    task.title,
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    textDecoration = if (task.status == TaskStatus.COMPLETED.name) TextDecoration.LineThrough else null
                )
                
                Spacer(modifier = Modifier.height(8.dp))
                
                // Chips Row
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                   AssistChip(
                       onClick = {},
                       label = { Text(stringResource(getPriorityLabel(task.priority))) },
                       leadingIcon = { Icon(StudyIcons.Flag, null, tint = MaterialTheme.colorScheme.primary) }
                   )
                   AssistChip(
                       onClick = {},
                       label = { Text(stringResource(getCategoryLabel(task.category))) },
                       leadingIcon = { Icon(StudyIcons.Category, null) }
                   )
                }

                Spacer(modifier = Modifier.height(16.dp))
                Divider()
                Spacer(modifier = Modifier.height(16.dp))

                // Description
                if (task.description.isNotBlank()) {
                    Text(
                        stringResource(StudyStrings.TaskDetailDescription),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        task.description,
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                }
                
                // Subject
                if (!task.subject.isNullOrBlank()) {
                     Row(verticalAlignment = Alignment.CenterVertically) {
                         Icon(Icons.AutoMirrored.Outlined.MenuBook, null, modifier = Modifier.size(20.dp))
                         Spacer(modifier = Modifier.width(8.dp))
                         Text("${stringResource(StudyStrings.TaskDetailSubject)} ${task.subject}", style = MaterialTheme.typography.bodyMedium)
                     }
                     Spacer(modifier = Modifier.height(8.dp))
                }

                // Deadline
                if (task.deadline != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Surface(
                        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                        shape = MaterialTheme.shapes.medium
                    ) {
                         Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.padding(12.dp).fillMaxWidth()
                        ) {
                            val isOverdue = task.deadline!! < Clock.System.now().toEpochMilliseconds() && task.status != TaskStatus.COMPLETED.name
                            Icon(
                                if (isOverdue) StudyIcons.StatsOverdue else StudyIcons.SessionTab, 
                                null, 
                                modifier = Modifier.size(24.dp),
                                tint = if (isOverdue) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary
                            )
                            Spacer(modifier = Modifier.width(12.dp))
                            Column {
                                Text(
                                    if (isOverdue) stringResource(StudyStrings.TaskDetailOverdue) else stringResource(StudyStrings.TaskDetailDueDate),
                                    style = MaterialTheme.typography.labelMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                                Text(
                                    formatDeadline(task.deadline!!), 
                                    style = MaterialTheme.typography.titleMedium,
                                    color = if (isOverdue) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class)
@Composable
fun TaskEditorDialog(
    task: Task? = null,
    onDismiss: () -> Unit,
    onSave: (Task) -> Unit
) {
    var title by remember { mutableStateOf(task?.title ?: "") }
    var description by remember { mutableStateOf(task?.description ?: "") }
    var priority by remember { mutableStateOf(task?.priority?.let { TaskPriority.valueOf(it) } ?: TaskPriority.MEDIUM) }
    var category by remember { mutableStateOf(task?.category?.let { TaskCategory.valueOf(it) } ?: TaskCategory.OTHER) }
    var recurrence by remember { mutableStateOf(task?.recurrence?.let { try { com.karyadina.model.TaskRecurrence.valueOf(it) } catch(e: Exception) { com.karyadina.model.TaskRecurrence.NONE } } ?: com.karyadina.model.TaskRecurrence.NONE) }
    var subject by remember { mutableStateOf(task?.subject ?: "") }
    var hasDeadline by remember { mutableStateOf(task?.deadline != null) }
    
    // Date & Time State
    // Default to end of today (23:59)
    val now = Clock.System.now()
    val systemTZ = TimeZone.currentSystemDefault()
    val today = now.toLocalDateTime(systemTZ).date
    
    // Default deadline: Today at 23:59 or existing deadline
    val initialDateTime = task?.deadline?.let { Instant.fromEpochMilliseconds(it).toLocalDateTime(systemTZ) }
    
    var selectedDate by remember { mutableStateOf(initialDateTime?.date ?: today) }
    var selectedTime by remember { mutableStateOf(initialDateTime?.time ?: LocalTime(23, 59)) }
    
    // Date Picker State
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = now.toEpochMilliseconds()
    )
    
    // Time Picker State
    var showTimePicker by remember { mutableStateOf(false) }
    val timePickerState = rememberTimePickerState(
        initialHour = 23,
        initialMinute = 59,
        is24Hour = isSystemIn24HourFormat()
    )

    if (showDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    datePickerState.selectedDateMillis?.let { millis ->
                        selectedDate = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.UTC).date
                    }
                    showDatePicker = false
                }) { Text(stringResource(StudyStrings.OK)) }
            },
            dismissButton = {
                TextButton(onClick = { showDatePicker = false }) { Text(stringResource(StudyStrings.Cancel)) }
            }
        ) {
            DatePicker(state = datePickerState)
        }
    }

    if (showTimePicker) {
        val timeState = rememberTimePickerState(
            initialHour = selectedTime.hour,
            initialMinute = selectedTime.minute,
            is24Hour = isSystemIn24HourFormat()
        )
        AlertDialog(
            onDismissRequest = { showTimePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    selectedTime = LocalTime(timeState.hour, timeState.minute)
                    showTimePicker = false
                }) { Text(stringResource(StudyStrings.OK)) }
            },
            dismissButton = {
                TextButton(onClick = { showTimePicker = false }) { Text(stringResource(StudyStrings.Cancel)) }
            },
            text = {
                TimePicker(state = timeState)
            }
        )
    }
    
    Dialog(
        onDismissRequest = onDismiss,
        properties = androidx.compose.ui.window.DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp),
            shape = MaterialTheme.shapes.extraLarge,
            color = MaterialTheme.colorScheme.surface,
            tonalElevation = 6.dp
        ) {
            Column(
                modifier = Modifier.fillMaxSize()
            ) {
                // Header
                Surface(
                    color = MaterialTheme.colorScheme.primaryContainer,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(if (task == null) StudyIcons.Add else StudyIcons.Edit, null, modifier = Modifier.size(28.dp), tint = MaterialTheme.colorScheme.onPrimaryContainer)
                            Text(if (task == null) stringResource(StudyStrings.TaskCreateTitle) else stringResource(StudyStrings.TaskEditTitle), style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.onPrimaryContainer)
                        }
                        IconButton(onClick = onDismiss) {
                            Icon(StudyIcons.Close, stringResource(StudyStrings.ActionClose), tint = MaterialTheme.colorScheme.onPrimaryContainer)
                        }
                    }
                }
                
                LazyColumn(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    contentPadding = PaddingValues(24.dp),
                    verticalArrangement = Arrangement.spacedBy(20.dp)
                ) {
                    // Title
                    item {
                        OutlinedTextField(
                            value = title,
                            onValueChange = { if (it.length <= 100) title = it },
                            label = { Text(stringResource(StudyStrings.TaskTitleLabel)) },
                            placeholder = { Text(stringResource(StudyStrings.TaskTitlePlaceholder)) },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            leadingIcon = { Icon(StudyIcons.SubjectTab, null) }, // Using Subject/Title icon
                            supportingText = {
                                Text(
                                    "${title.length}/100",
                                    modifier = Modifier.fillMaxWidth(),
                                    textAlign = androidx.compose.ui.text.style.TextAlign.End
                                )
                            }
                        )
                    }
                    
                    // Description
                    item {
                        OutlinedTextField(
                            value = description,
                            onValueChange = { description = it },
                            label = { Text(stringResource(StudyStrings.TaskDescLabel)) },
                            modifier = Modifier.fillMaxWidth(),
                            minLines = 3,
                            maxLines = 5,
                            leadingIcon = { Icon(Icons.Outlined.Description, null) }
                        )
                    }
                    
                    // Subject
                    item {
                        OutlinedTextField(
                            value = subject,
                            onValueChange = { if (it.length <= 50) subject = it },
                            label = { Text(stringResource(StudyStrings.TaskSubjectOptionalLabel)) },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            leadingIcon = { Icon(StudyIcons.SubjectTab, null) },
                            supportingText = {
                                Text(
                                    "${subject.length}/50",
                                    modifier = Modifier.fillMaxWidth(),
                                    textAlign = androidx.compose.ui.text.style.TextAlign.End
                                )
                            }
                        )
                    }
                    
                    // Deadline Picker
                    item {
                        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(stringResource(StudyStrings.TaskDeadlineLabel), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.SemiBold)
                                Switch(checked = hasDeadline, onCheckedChange = { hasDeadline = it })
                            }
                            
                            AnimatedVisibility(visible = hasDeadline) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                                ) {
                                    // Date Button
                                    OutlinedButton(
                                        onClick = { showDatePicker = true },
                                        modifier = Modifier.weight(1f)
                                    ) {
                                        Icon(StudyIcons.Date, null, modifier = Modifier.size(16.dp))
                                        Spacer(modifier = Modifier.width(8.dp))
                                        Text("${selectedDate.month.name.take(3)} ${selectedDate.dayOfMonth}, ${selectedDate.year}")
                                    }
                                    
                                    // Time Button
                                    OutlinedButton(
                                        onClick = { showTimePicker = true },
                                        modifier = Modifier.weight(1f)
                                    ) {
                                        Icon(StudyIcons.Time, null, modifier = Modifier.size(16.dp))
                                        Spacer(modifier = Modifier.width(8.dp))
                                        Text(
                                            "${selectedTime.hour.toString().padStart(2, '0')}:${selectedTime.minute.toString().padStart(2, '0')}"
                                        )
                                    }
                                }
                            }
                        }
                    }
                    
                    // Recurrence
                    item {
                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text(stringResource(StudyStrings.TaskRecurrenceLabel), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.SemiBold)
                            Row(
                                modifier = Modifier.horizontalScroll(rememberScrollState()),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                com.karyadina.model.TaskRecurrence.entries.forEach { option ->
                                    FilterChip(
                                        selected = recurrence == option,
                                        onClick = { recurrence = option },
                                        label = { Text(stringResource(getRecurrenceLabel(option.name))) },
                                        leadingIcon = if (recurrence == option) {
                                            { Icon(StudyIcons.Completed, null, modifier = Modifier.size(16.dp)) }
                                        } else null
                                    )
                                }
                            }
                        }
                    }
                    
                    // Priority
                    item {
                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text(stringResource(StudyStrings.TaskPriorityLabel), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.SemiBold)
                            androidx.compose.foundation.layout.FlowRow(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                TaskPriority.entries.forEach { p ->
                                    FilterChip(
                                        selected = priority == p,
                                        onClick = { priority = p },
                                        label = { Text(stringResource(getPriorityLabel(p.name))) },
                                        leadingIcon = if (priority == p) { { Icon(StudyIcons.Flag, null, modifier = Modifier.size(16.dp)) } } else null
                                    )
                                }
                            }
                        }
                    }
                    
                    // Category
                    item {
                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text(stringResource(StudyStrings.TaskCategoryLabel), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.SemiBold)
                            androidx.compose.foundation.layout.FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                TaskCategory.entries.forEach { c ->
                                    FilterChip(
                                        selected = category == c,
                                        onClick = { category = c },
                                        label = { Text(stringResource(getCategoryLabel(c.name))) }
                                    )
                                }
                            }
                        }
                    }
                }
                // Save Button
                Surface(
                    color = MaterialTheme.colorScheme.surface,
                    tonalElevation = 6.dp,
                    shadowElevation = 8.dp
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp)
                    ) {
                        Button(
                            onClick = {
                                if (title.isNotBlank()) {
                                    val deadlineTimestamp = if (hasDeadline) {
                                        val ldt = LocalDateTime(selectedDate, selectedTime)
                                        ldt.toInstant(TimeZone.currentSystemDefault()).toEpochMilliseconds()
                                    } else null
                                    
                                    onSave(
                                        Task(
                                            id = task?.id ?: "",
                                            updatedAt = task?.updatedAt ?: 0,
                                            title = title,
                                            description = description,
                                            priority = priority.name,
                                            category = category.name,
                                            subject = subject.ifBlank { null },
                                            deadline = deadlineTimestamp,
                                            //category = category.name,
                                            //subject = subject.ifBlank { null },
                                           // deadline = deadlineTimestamp,
                                            createdAt = task?.createdAt ?: 0, // Will be ignored if new
                                            recurrence = recurrence.name
                                        )
                                    )
                                }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            enabled = title.isNotBlank()
                        ) {
                            Icon(if (task == null) StudyIcons.Add else StudyIcons.Save, null)
                            Spacer(Modifier.width(8.dp))
                            Text(if (task == null) stringResource(StudyStrings.TaskCreateTitle) else stringResource(StudyStrings.TaskEditTitle)) // Reusing title strings for button or create separate if needed
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalTime::class)
@Composable
fun formatDeadline(timestamp: Long): String {
    val systemTZ = TimeZone.currentSystemDefault()
    val now = Clock.System.now()
    val deadline = Instant.fromEpochMilliseconds(timestamp)
    val nowLocal = now.toLocalDateTime(systemTZ)
    val deadlineLocal = deadline.toLocalDateTime(systemTZ)
    
    val daysUntil = (deadlineLocal.date.toEpochDays() - nowLocal.date.toEpochDays()).toLong()
    
    // Calculate simple duration for overdue/upcoming in hours/min if it's close
    val duration = deadline - now
    val durationHours = duration.inWholeHours
    val durationMinutes = duration.inWholeMinutes
    
    return when {
        durationMinutes < 0 -> {
            // Overdue
            val absHours = kotlin.math.abs(durationHours)
            val absDays = kotlin.math.abs(daysUntil)
            when {
                absDays > 0 -> stringResource(StudyStrings.TaskFormatOverdueDays, absDays)
                absHours > 0 -> stringResource(StudyStrings.TaskFormatOverdueHours, absHours)
                else -> stringResource(StudyStrings.TaskFormatOverdueMins, kotlin.math.abs(durationMinutes))
            }
        }
        daysUntil == 0L -> "${stringResource(StudyStrings.TaskFormatToday)} ${deadlineLocal.hour.toString().padStart(2,'0')}:${deadlineLocal.minute.toString().padStart(2,'0')}"
        daysUntil == 1L -> "${stringResource(StudyStrings.TaskFormatTomorrow)} ${deadlineLocal.hour.toString().padStart(2,'0')}:${deadlineLocal.minute.toString().padStart(2,'0')}"
        daysUntil < 7L -> stringResource(StudyStrings.TaskFormatDays, daysUntil)
        else -> "${deadlineLocal.dayOfMonth}/${deadlineLocal.monthNumber}"
    }
}

fun getPriorityLabel(priority: String): org.jetbrains.compose.resources.StringResource {
    return try {
        when (TaskPriority.valueOf(priority)) {
            TaskPriority.LOW -> StudyStrings.TaskPriorityLow
            TaskPriority.MEDIUM -> StudyStrings.TaskPriorityMedium
            TaskPriority.HIGH -> StudyStrings.TaskPriorityHigh
            TaskPriority.URGENT -> StudyStrings.TaskPriorityUrgent
        }
    } catch (e: Exception) {
        StudyStrings.TaskPriorityMedium
    }
}

fun getCategoryLabel(category: String): org.jetbrains.compose.resources.StringResource {
    return try {
        when (TaskCategory.valueOf(category)) {
            TaskCategory.ASSIGNMENT -> StudyStrings.TaskCategoryAssignment
            TaskCategory.EXAM_PREP -> StudyStrings.TaskCategoryExamPrep
            TaskCategory.PROJECT -> StudyStrings.TaskCategoryProject
            TaskCategory.READING -> StudyStrings.TaskCategoryReading
            TaskCategory.PRACTICE -> StudyStrings.TaskCategoryPractice
            TaskCategory.REVISION -> StudyStrings.TaskCategoryRevision
            TaskCategory.OTHER -> StudyStrings.TaskCategoryOther
        }
    } catch (e: Exception) {
        StudyStrings.TaskCategoryOther
    }
}

fun getRecurrenceLabel(recurrence: String): org.jetbrains.compose.resources.StringResource {
    return try {
        when (com.karyadina.model.TaskRecurrence.valueOf(recurrence)) {
            com.karyadina.model.TaskRecurrence.NONE -> StudyStrings.TaskRecurrenceNone
            com.karyadina.model.TaskRecurrence.DAILY -> StudyStrings.TaskRecurrenceDaily
            com.karyadina.model.TaskRecurrence.WEEKLY -> StudyStrings.TaskRecurrenceWeekly
            com.karyadina.model.TaskRecurrence.MONTHLY -> StudyStrings.TaskRecurrenceMonthly
        }
    } catch (e: Exception) {
        StudyStrings.TaskRecurrenceNone
    }
}
